import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Post } from '../types/interfaces';

const ROWS_PER_PAGE = 50;

interface GetAllPostsParams {
  page?: number;
  limit?: number;
  locationId?: string;
  excludeComments?: boolean;
  excludeExpired?: boolean;
  includeDeleted?: boolean;
  userFilter?: string[];
  mediaFilter?: string[];
  originLevelFilter?: string[];
  currentPostLevelFilter?: string[];
  locationNameFilter?: string[];
}

interface GetAllPostsResponse {
  data: Post[];
  total: number;
}

const fetchPosts = async (
  params: GetAllPostsParams
): Promise<GetAllPostsResponse> => {
  const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true'
    ? process.env.REACT_APP_REMOTE_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

  const queryParams = new URLSearchParams();
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params.locationId) queryParams.append('locationId', params.locationId);
  if (params.excludeComments) queryParams.append('excludeComments', 'true');
  if (params.excludeExpired) queryParams.append('excludeExpired', 'true');
  if (params.includeDeleted) queryParams.append('includeDeleted', 'true');
  if (params.userFilter && params.userFilter.length > 0) queryParams.append('userFilter', params.userFilter.join(','));
  if (params.mediaFilter && params.mediaFilter.length > 0) queryParams.append('mediaFilter', params.mediaFilter.join(','));
  if (params.originLevelFilter && params.originLevelFilter.length > 0) queryParams.append('originLevelFilter', params.originLevelFilter.join(','));
  if (params.currentPostLevelFilter && params.currentPostLevelFilter.length > 0) queryParams.append('currentPostLevelFilter', params.currentPostLevelFilter.join(','));
  if (params.locationNameFilter && params.locationNameFilter.length > 0) queryParams.append('locationNameFilter', params.locationNameFilter.join(','));

  const response = await fetch(`${apiBaseUrl}/api/posts/admin/all-posts?${queryParams.toString()}`, {
    credentials: 'include', // Required to send HttpOnly cookies
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch posts');
  }

  return response.json();
};

export const useInfinitePosts = (params: Omit<GetAllPostsParams, 'page' | 'limit'>) => {
  // Create a stable query key by serializing params
  const queryKey = useMemo(() => ['posts', 'infinite', params], [params]);
  
  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) => {
      return fetchPosts({
        ...params,
        page: pageParam,
        limit: ROWS_PER_PAGE,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      // If we got a full page of results, there might be more
      if (lastPage.data.length === ROWS_PER_PAGE) {
        const totalLoaded = allPages.reduce((sum, page) => sum + page.data.length, 0);
        // If total loaded is less than total, return next page number
        if (totalLoaded < lastPage.total) {
          const nextPage = allPages.length;
          return nextPage; // Return next page number (0-indexed, so length is next)
        }
      }
      // If we got less than a full page, or we've loaded everything, no more pages
      return undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
